import { ASSETS } from "@/lib/assets";
import { ArrowDownRight } from "lucide-react";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 lg:pt-48 pb-20 md:pb-28 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-12 gap-8 md:gap-12 items-end">
        {/* Left: Type */}
        <div className="col-span-12 lg:col-span-7 reveal">
          <div className="flex items-center gap-3 mb-8">
            <span className="block w-10 h-px bg-[#6B6B6B]" />
            <span className="text-[11px] tracking-[0.32em] uppercase text-[#A0A0A0] font-medium">
              Becoming H.E.R. LLC — Consulting &amp; Professional Support
            </span>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-serif text-[2.75rem] sm:text-6xl md:text-7xl lg:text-[7.5rem] leading-[0.95] tracking-tight text-[#EDEDED] font-light"
          >
            <span className="block">Honor.</span>
            <span
              className="block italic font-light"
              style={{
                background:
                  "linear-gradient(135deg, #E9CE94 0%, #C9A66B 50%, #8A7040 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Excellence.
            </span>
            <span className="block">
              Resilience<span className="text-[#C9A66B]">.</span>
            </span>
          </h1>

          <p className="mt-10 max-w-2xl text-base md:text-lg leading-relaxed text-[#A0A0A0] font-light">
            <span className="text-[#EDEDED]">
              Empowering excellence through strategic solutions.
            </span>{" "}
            We partner with organizations and communities to deliver{" "}
            <span className="text-[#EDEDED]">procurement</span>,{" "}
            <span className="text-[#EDEDED]">workforce development</span>, and{" "}
            <span className="text-[#EDEDED]">operational management</span> —
            built for sustainable, long-term growth.
          </p>

          <div className="mt-12 flex flex-wrap gap-4">
            <a
              href="#contact"
              data-testid="hero-cta-primary"
              className="bg-[#EDEDED] text-[#0A0A0A] hover:bg-[#C9A66B] hover:text-[#0A0A0A] transition-colors duration-300 px-8 py-4 text-[11px] tracking-[0.24em] uppercase cta-arrow"
            >
              Schedule a consultation
              <ArrowDownRight size={16} />
            </a>
            <a
              href="#pillars"
              data-testid="hero-cta-secondary"
              className="border border-[#EDEDED] text-[#EDEDED] hover:bg-[#EDEDED] hover:text-[#0A0A0A] transition-colors duration-300 px-8 py-4 text-[11px] tracking-[0.24em] uppercase"
            >
              Our four pillars
            </a>
          </div>

          {/* meta strip */}
          <div className="mt-16 grid grid-cols-3 max-w-lg border-t border-[#2A2A2A] pt-6">
            <div>
              <div className="font-serif text-3xl md:text-4xl text-[#EDEDED] font-light">
                12<span className="text-[#C9A66B]">+</span>
              </div>
              <div className="text-[10px] tracking-[0.24em] uppercase text-[#A0A0A0] mt-1">
                Years experience
              </div>
            </div>
            <div>
              <div className="font-serif text-3xl md:text-4xl text-[#EDEDED] font-light">
                100<span className="text-[#6B6B6B]">+</span>
              </div>
              <div className="text-[10px] tracking-[0.24em] uppercase text-[#A0A0A0] mt-1">
                Engagements delivered
              </div>
            </div>
            <div>
              <div className="font-serif text-3xl md:text-4xl text-[#EDEDED] font-light">
                04
              </div>
              <div className="text-[10px] tracking-[0.24em] uppercase text-[#A0A0A0] mt-1">
                Strategic pillars
              </div>
            </div>
          </div>
        </div>

        {/* Right: Official Logo */}
        <div className="col-span-12 lg:col-span-5 reveal">
          <div className="relative">
            {/* Ambient gold glow behind logo */}
            <div
              aria-hidden
              className="absolute -inset-8 rounded-full opacity-40 blur-3xl"
              style={{
                background:
                  "radial-gradient(ellipse at center, rgba(201,166,107,0.35) 0%, rgba(201,166,107,0) 65%)",
              }}
            />
            <div className="absolute -top-4 -left-4 right-8 bottom-8 border border-[#C9A66B]/25 hidden lg:block" />
            <div className="zoom-frame relative bg-[#000000] p-4 md:p-6 border border-[#C9A66B]/20">
              <img
                src={ASSETS.logoMonogram}
                alt="Becoming H.E.R. — Honor. Excellence. Resilience."
                data-testid="hero-monogram"
                className="w-full h-auto block"
                loading="eager"
              />
            </div>
            <div className="mt-4 flex items-center justify-between text-[10px] tracking-[0.28em] uppercase text-[#A0A0A0]">
              <span>The Emblem</span>
              <span className="text-[#C9A66B]">
                — Honor · Excellence · Resilience
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Marquee */}
      <div className="mt-24 md:mt-32 border-y border-[#2A2A2A] overflow-hidden">
        <div className="marquee-track whitespace-nowrap flex gap-16 py-5">
          {Array.from({ length: 2 }).map((_, dup) => (
            <div
              key={dup}
              className="flex gap-16 font-serif italic text-2xl md:text-3xl text-[#EDEDED] flex-shrink-0"
              aria-hidden={dup === 1}
            >
              {[
                "Procurement",
                "Workforce Development",
                "Operational Management",
                "Strategic Planning",
                "Governance",
                "Community Impact",
              ].map((w, i) => (
                <span key={`${dup}-${i}`} className="flex items-center gap-16">
                  {w}
                  <span
                    className={`not-italic ${
                      i % 2 === 0 ? "text-[#C9A66B]" : "text-[#6B6B6B]"
                    }`}
                  >
                    {i % 2 === 0 ? "✦" : "❦"}
                  </span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
